<template>
  <v-dialog v-model="dialog" max-width="900">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-map</v-icon>
        Gestione Appezzamenti
      </v-card-title>
      <v-card-text class="pa-0">
        <v-row no-gutters style="min-height: 400px;">
          <!-- Colonna sinistra: form -->
          <v-col cols="12" md="5" class="pa-6">
            <div class="d-flex align-center mb-4">
              <span class="text-subtitle-1 font-weight-medium">
                {{ appezzamento_in_modifica ? 'Modifica Appezzamento' : 'Nuovo Appezzamento' }}
              </span>
              <v-spacer />
              <v-btn v-if="appezzamento_in_modifica" text small color="grey" @click="annullaModifica">
                <v-icon small class="mr-1">mdi-close</v-icon>Annulla
              </v-btn>
            </div>

            <v-form ref="form" v-model="form_valid">
              <v-text-field
                v-model="form.nome"
                label="Nome"
                outlined
                dense
                :rules="[(v) => !!v || 'Campo obbligatorio']"
              />
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="form.larghezza"
                    label="Larghezza (m)"
                    type="number"
                    outlined
                    dense
                    :rules="[nonNegativeRule]"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="form.lunghezza"
                    label="Lunghezza (m)"
                    type="number"
                    outlined
                    dense
                    :rules="[nonNegativeRule]"
                  />
                </v-col>
              </v-row>
              <v-alert v-if="save_error" type="error" outlined dense class="mt-2">
                {{ save_error }}
              </v-alert>
            </v-form>

            <div class="d-flex justify-end mt-2">
              <v-btn text @click="closeDialog" class="mr-2">Chiudi</v-btn>
              <v-btn
                color="primary"
                :loading="loading_save"
                :disabled="!form_valid"
                @click="salva"
              >
                {{ appezzamento_in_modifica ? 'Aggiorna' : 'Salva' }}
              </v-btn>
            </div>
          </v-col>

          <v-divider vertical />

          <!-- Colonna destra: lista appezzamenti -->
          <v-col cols="12" md="7" class="pa-6 lista-col">
            <div class="d-flex align-center text-subtitle-1 font-weight-medium mb-4">
              <v-icon class="mr-2">mdi-map</v-icon>
              Appezzamenti disponibili
            </div>
            <v-alert v-if="delete_error" type="error" outlined dense class="mb-3">
              {{ delete_error }}
            </v-alert>
            <v-progress-linear v-if="loading_list" indeterminate class="mb-4" />
            <v-alert v-else-if="!appezzamenti.length" type="info" outlined dense>
              Nessun appezzamento configurato.
            </v-alert>
            <v-list v-else dense class="py-0">
              <v-list-item
                v-for="app in appezzamenti"
                :key="app.id"
                class="px-0 mb-1"
              >
                <v-list-item-avatar size="36" color="green lighten-5" rounded>
                  <v-icon color="green darken-1">mdi-map</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{ app.nome }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ app.larghezza || '?' }}×{{ app.lunghezza || '?' }} m
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="flex-column align-end" style="gap:4px">
                  <v-btn icon small color="primary" @click="apriModifica(app)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="red"
                    :loading="deleting_id === app.id"
                    :disabled="!!deleting_id"
                    @click="elimina(app)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "GestioneAppezzamentoDialog",
  data() {
    return {
      dialog: false,
      form_valid: false,
      loading_save: false,
      loading_list: false,
      deleting_id: null,
      save_error: "",
      delete_error: "",
      appezzamento_in_modifica: null,
      form: { nome: "", larghezza: null, lunghezza: null },
    };
  },
  computed: {
    appezzamenti() {
      return this.$store.getters.getAppezzamenti || [];
    },
  },
  methods: {
    openDialog() {
      this.dialog = true;
      this.delete_error = "";
      this.resetForm();
      this.fetchAppezzamenti();
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },
    fetchAppezzamenti() {
      this.loading_list = true;
      this.$store.dispatch("fetchAppezzamenti").finally(() => {
        this.loading_list = false;
      });
    },
    apriModifica(app) {
      this.appezzamento_in_modifica = app;
      this.save_error = "";
      this.form = {
        nome:      app.nome || "",
        larghezza: app.larghezza ?? null,
        lunghezza: app.lunghezza ?? null,
      };
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    annullaModifica() {
      this.appezzamento_in_modifica = null;
      this.save_error = "";
      this.resetForm();
    },
    salva() {
      if (!this.$refs.form.validate()) return;
      this.loading_save = true;
      this.save_error = "";
      const payload = {
        nome:      this.form.nome,
        larghezza: this.form.larghezza || null,
        lunghezza: this.form.lunghezza || null,
      };
      const action = this.appezzamento_in_modifica
        ? this.$store.dispatch("updateAppezzamento", {
            id: this.appezzamento_in_modifica.id,
            payload,
          })
        : this.$store.dispatch("saveAppezzamento", payload);

      action
        .then(() => {
          this.appezzamento_in_modifica = null;
          this.resetForm();
          this.$emit("appezzamenti-aggiornati");
        })
        .catch(() => {
          this.save_error = "Errore durante il salvataggio. Riprova.";
        })
        .finally(() => {
          this.loading_save = false;
        });
    },
    elimina(app) {
      this.deleting_id = app.id;
      this.delete_error = "";
      this.$store
        .dispatch("deleteAppezzamento", app.id)
        .then(() => {
          this.$emit("appezzamenti-aggiornati");
        })
        .catch((error) => {
          const status = error?.response?.status;
          this.delete_error =
            status === 403
              ? "Non hai i permessi per eliminare questo appezzamento."
              : "Errore durante l'eliminazione. Riprova.";
        })
        .finally(() => {
          this.deleting_id = null;
        });
    },
    resetForm() {
      this.form = { nome: "", larghezza: null, lunghezza: null };
      this.save_error = "";
      this.appezzamento_in_modifica = null;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    nonNegativeRule(value) {
      if (value === null || value === "" || value === undefined) return true;
      return Number(value) >= 0 || "Valore non valido";
    },
  },
};
</script>

<style scoped>
.lista-col {
  overflow-y: auto;
  max-height: 500px;
}
</style>
